import React, { useState, useEffect, useCallback } from 'react';
import {
  FiCheck as Check,
  FiX as X,
  FiAlertTriangle as AlertTriangle,
  FiRefreshCw as RefreshCw,
  FiBell as Bell,
  FiPlus as Plus,
  FiEdit2 as Edit2,
  FiTrash2 as Trash2,
  FiFilter as Filter,
  FiZap as Zap,
  FiChevronDown as ChevronDown,
  FiSettings as Settings,
  FiToggleLeft as ToggleOff,
  FiToggleRight as ToggleOn,
} from 'react-icons/fi';
import { notificationAPI } from '../../../services/realApi';
import Toggle from '../../../components/ui/Toggle';
import { MyAlertsPreferencesSectionProps, AlertPreference, AlertRuleItem } from './alertPreferences/types';
import {
  GENAIOPS_API_BASE,
  CATEGORY_CONFIG,
  CATEGORY_PRESETS,
  CHANNEL_ICONS,
  EMAIL_FREQUENCIES,
  SEVERITY_CONFIG,
  getMetricCategory,
  getAuthHeaders,
} from './alertPreferences/constants';
import { PreferenceConfigModal, AlertRuleModal, PresetModal } from './alertPreferences/Modals';

const MyAlertsPreferencesSection: React.FC<MyAlertsPreferencesSectionProps> = ({ clientId }) => {
  const [preferences, setPreferences] = useState<AlertPreference[]>([]);
  const [hasPreferenceChanges, setHasPreferenceChanges] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);

  const [alertRules, setAlertRules] = useState<AlertRuleItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRuleItem | null>(null);
  const [presetCategory, setPresetCategory] = useState<string | null>(null);
  const [showQuickSetupMenu, setShowQuickSetupMenu] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configCategory, setConfigCategory] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadPreferences(), loadAlertRules()]);
    } catch (err: unknown) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const result = await notificationAPI.getPreferences();
      if (result.success) {
        setPreferences(result.preferences || []);
      }
    } catch (err: unknown) {
      console.error('Error loading preferences:', err);
    }
  };

  const loadAlertRules = async () => {
    if (!clientId) return;
    try {
      const url = `${GENAIOPS_API_BASE}/alerts/rules?client_id=${clientId}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAlertRules(data || []);
      }
    } catch (err: unknown) {
      console.error('Error loading alert rules:', err);
    }
  };

  const getPreferenceForCategory = (category: string): AlertPreference => {
    return preferences.find(p => p.notification_category === category) || {
      notification_category: category,
      in_app_enabled: true,
      email_enabled: true,
      slack_enabled: false,
      email_frequency: 'immediate',
    };
  };

  const updatePreference = (category: string, field: string, value: boolean | string) => {
    setHasPreferenceChanges(true);
    setPreferences(prev => {
      const existing = prev.find(p => p.notification_category === category);
      if (existing) {
        return prev.map(p =>
          p.notification_category === category
            ? { ...p, [field]: value }
            : p
        );
      } else {
        return [...prev, {
          notification_category: category,
          in_app_enabled: true,
          email_enabled: true,
          slack_enabled: false,
          email_frequency: 'immediate',
          [field]: value,
        }];
      }
    });
  };

  const handleSavePreferences = async () => {
    setSavingPreferences(true);
    setError(null);
    try {
      const result = await notificationAPI.updatePreferences(preferences);
      if (result.success) {
        setSuccess('Preferences saved successfully');
        setHasPreferenceChanges(false);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to save preferences');
      }
    } catch (err: unknown) {
      console.error('Error saving preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleToggleCategoryEnabled = (category: string, enabled: boolean) => {
    updatePreference(category, 'in_app_enabled', enabled);
    updatePreference(category, 'email_enabled', enabled);
    updatePreference(category, 'slack_enabled', enabled);
  };

  const isCategoryEnabled = (pref: AlertPreference): boolean => {
    return pref.in_app_enabled || pref.email_enabled || pref.slack_enabled;
  };

  const handleSaveRule = async (ruleData: Record<string, unknown>) => {
    try {
      const baseUrl = editingRule
        ? `${GENAIOPS_API_BASE}/alerts/rules/${editingRule.id}`
        : `${GENAIOPS_API_BASE}/alerts/rules`;
      const url = clientId ? `${baseUrl}?client_id=${clientId}` : baseUrl;

      const { client_id, ...bodyData } = ruleData;
      bodyData.channel_ids = [];

      const res = await fetch(url, {
        method: editingRule ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(bodyData)
      });

      if (res.ok) {
        await loadAlertRules();
        setShowRuleModal(false);
        setEditingRule(null);
        setSuccess(editingRule ? 'Rule updated' : 'Rule created');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const error = await res.json();
        setError(`Failed to save rule: ${JSON.stringify(error.detail) || 'Unknown error'}`);
      }
    } catch (err: unknown) {
      console.error('Failed to save rule:', err);
      setError('Failed to save rule');
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!globalThis.confirm('Delete this alert rule?')) return;
    try {
      const url = clientId
        ? `${GENAIOPS_API_BASE}/alerts/rules/${ruleId}?client_id=${clientId}`
        : `${GENAIOPS_API_BASE}/alerts/rules/${ruleId}`;
      await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      await loadAlertRules();
      setSuccess('Rule deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to delete rule:', err);
      setError('Failed to delete rule');
    }
  };

  const handleToggleRule = async (rule: AlertRuleItem) => {
    try {
      const url = clientId
        ? `${GENAIOPS_API_BASE}/alerts/rules/${rule.id}?client_id=${clientId}`
        : `${GENAIOPS_API_BASE}/alerts/rules/${rule.id}`;
      await fetch(url, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_enabled: !rule.is_enabled })
      });
      await loadAlertRules();
    } catch (err: unknown) {
      console.error('Failed to toggle rule:', err);
    }
  };

  const parsePresetCondition = (condition: string): { operator: string; threshold: number } => {
    if (condition === 'anomaly') {
      return { operator: 'anomaly', threshold: 0 };
    }
    const match = condition.match(/([<>=]+)\s*[$]?([\d.]+)(k)?/i);
    if (match) {
      const operatorMap: Record<string, string> = {
        '>': 'gt',
        '<': 'lt',
        '>=': 'gte',
        '<=': 'lte',
        '=': 'eq',
      };
      let threshold = Number.parseFloat(match[2]);
      if (match[3]?.toLowerCase() === 'k') {
        threshold *= 1000;
      }
      return { operator: operatorMap[match[1]] || 'gt', threshold };
    }
    return { operator: 'gt', threshold: 0 };
  };

  const handleApplyPreset = async (presetName: string) => {
    let preset = null;
    for (const category of Object.keys(CATEGORY_PRESETS)) {
      const found = CATEGORY_PRESETS[category].find(p => p.name === presetName);
      if (found) {
        preset = found;
        break;
      }
    }
    if (!preset) {
      setError(`Preset '${presetName}' not found`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      const { operator, threshold } = parsePresetCondition(preset.condition);
      const ruleData = {
        name: preset.label,
        description: preset.description,
        metric: preset.metric,
        operator,
        threshold,
        severity: preset.severity,
        channel_ids: [],
        is_enabled: true,
        cooldown_seconds: 900,
        duration_seconds: 300,
      };

      const url = clientId
        ? `${GENAIOPS_API_BASE}/alerts/rules?client_id=${clientId}`
        : `${GENAIOPS_API_BASE}/alerts/rules`;

      const res = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(ruleData),
      });

      if (!res.ok) {
        const error = await res.json();
        setError(`Failed to create rule: ${JSON.stringify(error.detail) || 'Unknown error'}`);
        return;
      }

      await loadAlertRules();
      setSuccess(`Rule "${preset.label}" created!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to apply preset:', err);
      setError('Failed to create rule from preset');
    }
  };

  const handleApplyMultiplePresets = async (presetNames: string[]) => {
    setPresetCategory(null);
    let successCount = 0;
    let errorCount = 0;

    for (const presetName of presetNames) {
      try {
        await handleApplyPreset(presetName);
        successCount++;
      } catch {
        errorCount++;
      }
    }

    if (successCount > 0) {
      await loadAlertRules();
      setSuccess(`${successCount} rule${successCount > 1 ? 's' : ''} created successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    }
    if (errorCount > 0) {
      setError(`${errorCount} rule${errorCount > 1 ? 's' : ''} failed to create`);
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleBatchToggle = useCallback(async (enableAll: boolean) => {
    const categories = Object.keys(CATEGORY_CONFIG);
    categories.forEach(category => {
      handleToggleCategoryEnabled(category, enableAll);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCategoryColor = (category: string): string => {
    const config = CATEGORY_CONFIG[category];
    const colorMap = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      cyan: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
      indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      gray: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    };
    return colorMap[config?.color as keyof typeof colorMap] || colorMap.gray;
  };

  const getRulesForCategory = (category: string): AlertRuleItem[] => {
    return alertRules.filter(rule => getMetricCategory(rule.metric || '') === category);
  };

  const getActiveChannels = (pref: AlertPreference): string[] => {
    const channels = [];
    if (pref.in_app_enabled) channels.push('in_app');
    if (pref.email_enabled) channels.push('email');
    if (pref.slack_enabled) channels.push('slack');
    return channels;
  };

  const categories = Object.keys(CATEGORY_CONFIG);
  const filteredCategories = selectedCategory === 'all'
    ? categories
    : categories.filter(c => c === selectedCategory);

  const groupedData = filteredCategories.map(category => ({
    category,
    config: CATEGORY_CONFIG[category],
    preference: getPreferenceForCategory(category),
    rules: getRulesForCategory(category),
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="text-green-800 dark:text-green-300">{success}</p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="text-sm pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer focus:outline-none"
              aria-label="Filter by category"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_CONFIG[cat]?.name || cat}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1 border-l border-gray-200 dark:border-gray-600 pl-3">
            <button
              onClick={() => handleBatchToggle(true)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
              aria-label="Enable all preferences"
            >
              <ToggleOn className="w-3.5 h-3.5" />
              Enable All
            </button>
            <button
              onClick={() => handleBatchToggle(false)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
              aria-label="Disable all preferences"
            >
              <ToggleOff className="w-3.5 h-3.5" />
              Disable All
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasPreferenceChanges && (
            <button
              onClick={handleSavePreferences}
              disabled={savingPreferences}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {savingPreferences ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          )}

          <div className="relative">
            <button
              onClick={() => setShowQuickSetupMenu(!showQuickSetupMenu)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Zap className="w-4 h-4" />
              Quick Setup
              <ChevronDown className="w-4 h-4" />
            </button>
            {showQuickSetupMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <div className="p-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1">Select a category:</p>
                  {Object.entries(CATEGORY_PRESETS).map(([category, presets]) => {
                    const config = CATEGORY_CONFIG[category];
                    const Icon = config?.icon || Zap;
                    return (
                      <button
                        key={category}
                        onClick={() => {
                          setShowQuickSetupMenu(false);
                          setPresetCategory(category);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                      >
                        <Icon className="w-4 h-4" />
                        <span>{config?.name || category}</span>
                        <span className="ml-auto text-xs text-gray-400">{presets.length} presets</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => {
              setEditingRule(null);
              setShowRuleModal(true);
            }}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Rule
          </button>

          <button
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Notification
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Severity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Channels
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Frequency
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Enabled
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Configure
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {groupedData.map(({ category, config, preference, rules }) => (
              <React.Fragment key={category}>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td colSpan={6} className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <span className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
                        <config.icon className="w-4 h-4" />
                      </span>
                      <div>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {config.name}
                        </span>
                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                          {1 + rules.length} {1 + rules.length === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>

                <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                  isCategoryEnabled(preference) ? '' : 'opacity-60'
                }`}>
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {config.name} Notifications
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {config.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>{' '}
                      Preference
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {getActiveChannels(preference).map((channel) => {
                        const channelInfo = CHANNEL_ICONS[channel as keyof typeof CHANNEL_ICONS];
                        if (!channelInfo) return null;
                        const Icon = channelInfo.icon;
                        return (
                          <span
                            key={channel}
                            className={`p-1.5 rounded-md ${channelInfo.bgColor}`}
                            title={channelInfo.label}
                          >
                            <Icon className={`w-3.5 h-3.5 ${channelInfo.iconColor}`} />
                          </span>
                        );
                      })}
                      {getActiveChannels(preference).length === 0 && (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {preference.email_enabled ? (
                      EMAIL_FREQUENCIES.find(f => f.value === preference.email_frequency)?.label || 'Immediate'
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Toggle
                      checked={isCategoryEnabled(preference)}
                      onChange={(enabled) => handleToggleCategoryEnabled(category, enabled)}
                      label={`${isCategoryEnabled(preference) ? 'Disable' : 'Enable'} ${config.name} notifications`}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => {
                        setConfigCategory(category);
                        setShowConfigModal(true);
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Configure notification preferences"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </td>
                </tr>

                {rules.map((rule) => {
                  const severity = SEVERITY_CONFIG[rule.severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.info;

                  return (
                    <tr
                      key={rule.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        rule.is_enabled ? '' : 'opacity-60'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-amber-500" />
                            <span className="font-medium text-gray-900 dark:text-white">
                              {rule.name}
                            </span>
                          </div>
                          {rule.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 ml-6">
                              {rule.description.substring(0, 60)}
                              {rule.description.length > 60 ? '...' : ''}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${severity.bgColor} ${severity.textColor}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${severity.dotColor}`}></span>
                          {severity.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {rule.operator === 'anomaly' ? 'Anomaly' : `${rule.metric} ${rule.operator} ${rule.threshold}`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        —
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Toggle
                          checked={rule.is_enabled}
                          onChange={() => handleToggleRule(rule)}
                          label={`${rule.is_enabled ? 'Disable' : 'Enable'} ${rule.name}`}
                          size="sm"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => {
                              setEditingRule(rule);
                              setShowRuleModal(true);
                            }}
                            className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Edit rule"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            className="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Delete rule"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {groupedData.length === 0 && !loading && (
          <div className="text-center py-12">
            <Bell className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No items found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No preferences or rules match the selected filter.
            </p>
          </div>
        )}
      </div>

      {hasPreferenceChanges && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <p className="text-amber-800 dark:text-amber-300">
              You have unsaved changes. Click "Save" to apply your preferences.
            </p>
          </div>
        </div>
      )}

      {showConfigModal && configCategory && (
        <PreferenceConfigModal
          config={CATEGORY_CONFIG[configCategory]}
          preference={getPreferenceForCategory(configCategory)}
          onSave={(updates) => {
            Object.entries(updates).forEach(([field, value]) => {
              updatePreference(configCategory, field, value as string | boolean);
            });
            setShowConfigModal(false);
            setConfigCategory(null);
          }}
          onClose={() => {
            setShowConfigModal(false);
            setConfigCategory(null);
          }}
        />
      )}

      {showRuleModal && (
        <AlertRuleModal
          rule={editingRule}
          onSave={handleSaveRule}
          onClose={() => {
            setShowRuleModal(false);
            setEditingRule(null);
          }}
        />
      )}

      {presetCategory && (
        <PresetModal
          category={presetCategory}
          categoryPresets={CATEGORY_PRESETS[presetCategory] || []}
          onApplyMultiple={handleApplyMultiplePresets}
          onClose={() => setPresetCategory(null)}
        />
      )}
    </div>
  );
};

export default MyAlertsPreferencesSection;
